if (!customElements.get('sticky-add-to-cart')) {
  class StickyAddToCart extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
    }

    connectedCallback() {
      if (this.initialized) return;

      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        this.init();
      });
    }

    init() {
      const sectionId = this.dataset.sectionId;

      // Find the main add to cart button - try multiple selectors
      this.mainAddToCartButton = document.querySelector(`#ProductSubmitButton-${sectionId}`) ||
                                  document.querySelector('.product-form__submit') ||
                                  document.querySelector('[name="add"]');

      this.stickyButton = this.querySelector('.sticky-atc__button');
      this.productInfo = document.querySelector('product-info');
      this.mainProductForm = document.querySelector(`#ProductInfo-${sectionId}`) ||
                             document.querySelector('.product-form form');

      console.log('Sticky ATC Debug:', {
        sectionId,
        mainAddToCartButton: this.mainAddToCartButton,
        stickyButton: this.stickyButton,
        mainProductForm: this.mainProductForm
      });

      if (!this.mainAddToCartButton || !this.stickyButton) {
        console.warn('Sticky ATC: Required elements not found');
        return;
      }

      this.initialized = true;
      this.setupIntersectionObserver();
      this.setupFooterObserver();
      this.setupEventListeners();
      this.updateStickyButtonState();
      this.syncQuantity();
    }

    setupIntersectionObserver() {
      // Show sticky button when main button is not visible
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.1
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.hide();
          } else {
            this.show();
          }
        });
      }, options);

      this.observer.observe(this.mainAddToCartButton);
    }

    setupFooterObserver() {
      // Fade the bar out once the footer scrolls into view, so it never covers
      // the footer content. Independent of the main-button slide logic.
      const footer = document.querySelector('.footer-custom') ||
                     document.querySelector('footer');
      if (!footer) return;

      this.footerObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          this.classList.toggle('footer-in-view', entry.isIntersecting);
        });
      }, { root: null, rootMargin: '0px', threshold: 0 });

      this.footerObserver.observe(footer);
    }

    setupEventListeners() {
      // Sync sticky button click with main form submission
      this.stickyButton.addEventListener('click', this.handleAddToCart.bind(this));

      // Listen for variant changes
      if (this.productInfo) {
        this.productInfo.addEventListener('variant-change', this.handleVariantChange.bind(this));
      }

      // Listen for main form updates
      if (this.mainProductForm) {
        const form = this.mainProductForm.querySelector('form') || this.mainProductForm;
        form.addEventListener('submit', () => {
          this.setLoading(true);
        });
      }

      // Listen for cart updates to reset loading state
      document.addEventListener('cart:updated', () => {
        this.setLoading(false);
      });

      document.addEventListener('cart:item-added', () => {
        this.setLoading(false);
      });

      // Quantity input sync
      const mainQuantityInput = document.querySelector('[name="quantity"]');
      const stickyQuantityInput = this.querySelector('.sticky-atc__quantity-input');

      if (mainQuantityInput && stickyQuantityInput) {
        mainQuantityInput.addEventListener('change', () => {
          stickyQuantityInput.value = mainQuantityInput.value;
        });

        stickyQuantityInput.addEventListener('change', () => {
          mainQuantityInput.value = stickyQuantityInput.value;
        });
      }

      // Quantity buttons
      const decreaseBtn = this.querySelector('[data-action="decrease"]');
      const increaseBtn = this.querySelector('[data-action="increase"]');

      if (decreaseBtn) {
        decreaseBtn.addEventListener('click', () => this.changeQuantity(-1));
      }

      if (increaseBtn) {
        increaseBtn.addEventListener('click', () => this.changeQuantity(1));
      }
    }

    handleAddToCart(event) {
      event.preventDefault();

      if (this.stickyButton.disabled) return;

      // Scroll to the main add to cart button at the top
      if (this.mainAddToCartButton) {
        // Smooth scroll to the button
        this.mainAddToCartButton.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });

        // After scrolling, focus on the button for better UX
        setTimeout(() => {
          this.mainAddToCartButton.focus();

          // Optional: Add a subtle highlight effect
          this.mainAddToCartButton.style.transition = 'transform 0.2s ease';
          this.mainAddToCartButton.style.transform = 'scale(1.02)';

          setTimeout(() => {
            this.mainAddToCartButton.style.transform = 'scale(1)';
          }, 200);
        }, 500);
      }
    }

    handleVariantChange(event) {
      const variant = event.detail?.variant;

      if (!variant) {
        this.stickyButton.disabled = true;
        const textSpan = this.stickyButton.querySelector('span');
        if (textSpan) textSpan.textContent = 'Unavailable';
        return;
      }

      // Update price
      const priceElement = this.querySelector('.sticky-atc__price');
      if (priceElement && variant.price) {
        priceElement.textContent = this.formatMoney(variant.price);
      }

      // Update variant display
      const variantElement = this.querySelector('.sticky-atc__variant');
      if (variantElement && variant.options) {
        const variantText = variant.options.filter(opt => opt !== 'Default Title').join(' / ');
        variantElement.textContent = variantText;
        variantElement.style.display = variantText ? 'block' : 'none';
      }

      // Update image
      const imageElement = this.querySelector('.sticky-atc__image img');
      if (imageElement && variant.featured_image?.src) {
        imageElement.src = variant.featured_image.src;
      }

      // Update button state
      this.updateStickyButtonState();
    }

    updateStickyButtonState() {
      if (!this.mainAddToCartButton) return;

      const isDisabled = this.mainAddToCartButton.disabled;
      const buttonTextEl = this.mainAddToCartButton.querySelector('.product-form__submit-text');
      const buttonText = buttonTextEl?.textContent?.trim() || 'Add to Cart';

      this.stickyButton.disabled = isDisabled;

      // Update button text
      const textSpan = this.stickyButton.querySelector('span');
      if (textSpan && !this.stickyButton.classList.contains('loading')) {
        textSpan.textContent = buttonText;
      }
    }

    syncQuantity() {
      const mainQuantityInput = document.querySelector('[name="quantity"]');
      const stickyQuantityInput = this.querySelector('.sticky-atc__quantity-input');

      if (mainQuantityInput && stickyQuantityInput) {
        stickyQuantityInput.value = mainQuantityInput.value || 1;
      }
    }

    changeQuantity(change) {
      const input = this.querySelector('.sticky-atc__quantity-input');
      const mainInput = document.querySelector('[name="quantity"]');

      if (!input) return;

      const currentValue = parseInt(input.value) || 1;
      const newValue = Math.max(1, currentValue + change);

      input.value = newValue;
      if (mainInput) {
        mainInput.value = newValue;
        mainInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    setLoading(loading) {
      if (loading) {
        this.stickyButton.classList.add('loading');
        this.stickyButton.disabled = true;
      } else {
        this.stickyButton.classList.remove('loading');
        this.updateStickyButtonState();
      }
    }

    show() {
      this.classList.add('visible');
    }

    hide() {
      this.classList.remove('visible');
    }

    formatMoney(cents) {
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
      }
      return '$' + (cents / 100).toFixed(2);
    }

    updateCartCount() {
      fetch(window.Shopify?.routes?.root + 'cart.js' || '/cart.js')
        .then(response => response.json())
        .then(cart => {
          const cartCountElements = document.querySelectorAll('.cart-count-bubble, [data-cart-count]');
          cartCountElements.forEach(element => {
            element.textContent = cart.item_count;
            if (cart.item_count > 0) {
              element.classList.remove('hidden');
            }
          });

          // Trigger cart update event
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        })
        .catch(err => console.error('Error updating cart count:', err));
    }

    showSuccess(message) {
      this.showNotification(message, 'success');
    }

    showError(message) {
      this.showNotification(message, 'error');
    }

    showNotification(message, type = 'success') {
      const toast = document.createElement('div');
      toast.className = `sticky-atc-notification sticky-atc-notification--${type}`;
      toast.textContent = message;
      toast.style.cssText = `
        position: fixed;
        bottom: calc(var(--sticky-atc-height, 80px) + 20px);
        left: 50%;
        transform: translateX(-50%);
        background: ${type === 'error' ? 'rgb(var(--color-error, 200, 0, 0))' : 'rgb(var(--color-success, 0, 150, 0))'};
        color: white;
        padding: 1.2rem 2.4rem;
        border-radius: var(--buttons-radius, 4px);
        z-index: 101;
        animation: fadeInOut 3s ease;
        font-size: 1.4rem;
        font-weight: 500;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      `;

      document.body.appendChild(toast);

      setTimeout(() => {
        toast.remove();
      }, 3000);
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }
      if (this.footerObserver) {
        this.footerObserver.disconnect();
      }
    }
  }

  customElements.define('sticky-add-to-cart', StickyAddToCart);
}

// Add notification animation
if (!document.getElementById('sticky-atc-styles')) {
  const style = document.createElement('style');
  style.id = 'sticky-atc-styles';
  style.textContent = `
    @keyframes fadeInOut {
      0%, 100% { opacity: 0; transform: translateX(-50%) translateY(10px); }
      10%, 90% { opacity: 1; transform: translateX(-50%) translateY(0); }
    }
  `;
  document.head.appendChild(style);
}
